
import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Register() {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [phone, setPhone] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [verifying, setVerifying] = useState(false);
    const [otp, setOtp] = useState('');
    const navigate = useNavigate();

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const { error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: {
                    phone: phone
                }
            }
        });

        if (error) {
            setError(error.message);
        } else {
            // Instead of navigating, we switch to Verification Mode
            setVerifying(true);
            alert('Code sent! Please check your email.');
        }
        setLoading(false);
    };

    const handleVerifyArgs = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const { error } = await supabase.auth.verifyOtp({
            email,
            token: otp,
            type: 'signup'
        });

        if (error) {
            setError(error.message);
        } else {
            alert('Email verified! Redirecting to login...');
            
            // Google Ads Sign-up Conversion Tracking
            try {
                if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
                    (window as any).gtag('event', 'conversion', {
                        'send_to': 'AW-999773323/bzKfCL67iKQcEIup3dwD',
                        'value': 50.0,
                        'currency': 'EGP'
                    });
                }
            } catch (err) {
                console.error("Failed to fire conversion tag", err);
            }

            navigate('/app');
        }
        setLoading(false);
    }

    if (verifying) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-stone-950 text-white relative overflow-hidden">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-500/10 blur-[100px] rounded-full" />

                <div className="bg-stone-900/80 backdrop-blur-xl p-8 rounded-3xl shadow-2xl w-full max-w-md border border-stone-800 relative z-10">
                    <h2 className="text-2xl font-bold mb-6 text-center text-white">{t('auth.verify_title')}</h2>
                    <p className="text-center text-stone-400 text-sm mb-6">{t('auth.verify_desc')} {email}</p>
                    {error && <div className="bg-red-900/50 text-red-200 p-3 rounded-xl mb-4 text-sm border border-red-800/50">{error}</div>}
                    <form onSubmit={handleVerifyArgs} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-stone-400 mb-1">{t('auth.verify_code')}</label>
                            <input
                                type="text"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                className="w-full bg-black border border-stone-800 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-center text-2xl tracking-widest text-white transition-all shadow-inner"
                                placeholder="000000"
                                required
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 px-4 rounded-xl transition-all shadow-lg shadow-indigo-900/20 hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
                        >
                            {loading ? t('auth.verifying') : t('auth.verify_btn')}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-stone-950 text-white relative overflow-hidden">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-indigo-500/10 blur-[100px] rounded-full" />

            <div className="bg-stone-900/80 backdrop-blur-xl p-8 rounded-3xl shadow-2xl w-full max-w-md border border-stone-800 relative z-10">
                <h2 className="text-2xl font-bold mb-8 text-center text-white space-x-2">
                    <span className="text-indigo-500 tracking-widest uppercase text-lg">Dotting</span>
                </h2>
                {error && <div className="bg-red-900/50 text-red-200 p-3 rounded-xl mb-6 text-sm border border-red-800/50">{error}</div>}

                <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                        <label className="block text-xs uppercase tracking-wider font-bold text-stone-500 mb-2">{t('auth.email')}</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full bg-black border border-stone-800 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent focus:outline-none text-white transition-all shadow-inner"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-wider font-bold text-stone-500 mb-2">{t('auth.phone')}</label>
                        <input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className="w-full bg-black border border-stone-800 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent focus:outline-none text-white transition-all shadow-inner"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-xs uppercase tracking-wider font-bold text-stone-500 mb-2">{t('auth.password')}</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-black border border-stone-800 rounded-xl p-3 focus:ring-2 focus:ring-indigo-500 focus:border-transparent focus:outline-none text-white transition-all shadow-inner"
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-white hover:bg-gray-200 text-black font-bold py-3 px-4 rounded-xl transition-all shadow-[0_0_20px_-5px_rgba(255,255,255,0.3)] mt-6 hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
                    >
                        {loading ? t('auth.registering') : t('auth.register_btn')}
                    </button>
                </form>

                <p className="mt-8 text-center text-sm text-stone-500">
                    {t('auth.has_account')} <Link to="/login" className="text-white font-medium hover:text-indigo-400 transition-colors ml-1">{t('auth.login_link')}</Link>
                </p>
            </div>
        </div>
    );
}
